import { DynamicForm, FormService, useApiService, validators } from "@art/shared-ui";
import { RootState, updateAppState } from '../../../store';
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { usePageWrapper } from '../../../hooks/page-wrapper.hook';
import { Box, Typography, Switch, Alert, Divider } from "@mui/material";
import { useAttributePermission } from "../../permissions";

type SettingKey = 'anonymousConnectRestricted' | 'namespaceEnabled';

interface SettingConfig {
    key: SettingKey;
    endpoint: string;
    permission: boolean;
    dataQa: string;
    title: string;
    description: string;
    enabledLabel: string;
    disabledLabel: string;
    successMessage: (enabled: boolean) => string;
    infoMessage?: string;
}


export default usePageWrapper(function AppSettings() {
    const appState = useSelector((state: RootState) => state.appstate);
    const apiService = useApiService();
    const dispatch = useDispatch();
    const [formService, setFormService] = useState<FormService | null>(null);
    const [organisationSettings, setOrganisationSettings] = useState<Record<SettingKey, boolean>>({
        anonymousConnectRestricted: false,
        namespaceEnabled: false,
    });
    const [isSaving, setIsSaving] = useState(false);
    const canManageAppDefaults = useAttributePermission("APP_MANAGEMENT", "APP_CONFIGURATION", "ManageAppDefaults");
    const canManageAnonymousConnections = useAttributePermission("APP_MANAGEMENT", "APP_CONFIGURATION", "ManageAnonymousConnections");
    const canManageChannelNamespaces = useAttributePermission("APP_MANAGEMENT", "APP_CONFIGURATION", "ManageChannelNamespaces");

    const settingsConfig: SettingConfig[] = [
        {
            key: 'anonymousConnectRestricted',
            endpoint: '/organisation/settings/anonymous-connections',
            permission: canManageAnonymousConnections,
            dataQa: 'restrict-anonymous-toggle',
            title: 'Restrict Anonymous Connections',
            description: 'When enabled, anonymous SDK connections using Client ID and Client Secret (client credentials) will be blocked. Only authenticated users with valid passcodes or admin-level credentials will be able to connect.',
            enabledLabel: 'Restricted',
            disabledLabel: 'Unrestricted',
            successMessage: (enabled) => `Anonymous connections ${enabled ? 'restricted' : 'allowed'} successfully`,
            infoMessage: 'Changes take effect immediately for new connections. Existing active connections will not be affected.',
        },
        {
            key: 'namespaceEnabled',
            endpoint: '/organisation/settings/channel-namespaces',
            permission: canManageChannelNamespaces,
            dataQa: 'channel-namespaces-toggle',
            title: 'Enable Channel Namespaces',
            description: 'When enabled namespace will be auto created for each channel which can be subscribed to individually.',
            enabledLabel: 'Enabled',
            disabledLabel: 'Disabled',
            successMessage: (enabled) => `Channel namespaces ${enabled ? 'enabled' : 'disabled'} successfully`,
        },
    ];

    const fetchSettings = async () => {
        try {
            const allowedSettings = settingsConfig.filter((config) => config.permission);
            const responses = await Promise.all(allowedSettings.map((config) => apiService.get(config.endpoint, {
                showErrorSnack: false,
            })));
            setOrganisationSettings((current) => {
                const next = { ...current };
                allowedSettings.forEach((config, index) => {
                    next[config.key] = responses[index]?.data?.data?.enabled ?? false;
                });
                return next;
            });
        } catch (error: any) {
            console.error("Failed to fetch organisation settings:", error);
        }
    };

    const handleSettingToggle = async (settingKey: SettingKey, config: SettingConfig) => {
        const newValue = !organisationSettings[settingKey];
        setIsSaving(true);
        
        try {
            await apiService.put(config.endpoint, { enabled: newValue });
            
            setOrganisationSettings(prev => ({
                ...prev,
                [settingKey]: newValue,
            }));
            
            apiService.showSnack(config.successMessage(newValue), 'success');
        } catch (error: any) {
            apiService.showSnack(
                error?.response?.data?.message || "Failed to update setting",
                "error"
            );
        } finally {
            setIsSaving(false);
        }
    };


    useEffect(() => {
        fetchSettings();
        if (appState.environment && appState.project_key && formService) {
            formService?.setFormData({
                "project_key": appState.project_key,
                "title": appState.environment,
            });
        }

    }, [appState, formService])
    const formBuilder = (data: any) => {
        return [
            {
                type: 'row',
                children: [
                    {
                        type: 'col',
                        classNames: ['col-12', 'col-md-6'],
                        children: [
                            {
                                id: "selected_project",
                                label: "Project Name",
                                type: "select",
                                // placeholder: "select project name",
                                required: false,
                                value: data?.project_key,
                                getOptions: async () => {
                                    const response = await apiService.get("/projects?limit=100");
                                    return response.data.data.projects.map((item: any) => ({
                                        label: item.title,
                                        value: item.project_key
                                    }));
                                },

                                validate: validators((value) => {
                                    if (!value || value.trim() === "") {
                                        return "Project name is required.";
                                    }
                                    return null;
                                })
                            },
                        ]
                    }
                ]
            },
            {
                type: 'row',
                children: [
                    {
                        type: 'col',
                        classNames: ['col-12', 'col-md-6'],
                        children: [
                            {
                                id: "selected_environment",
                                label: "Environment Name",
                                type: "select",
                                // placeholder: "Select environment name",
                                required: false,
                                value: data?.title,
                                getOptions: async () => {
                                    const response = await apiService.get("/environments");
                                    return response.data.data.environments.map((item: any) => ({
                                        label: item.title,
                                        value: item.title
                                    }));
                                },
                                validate: validators((value) => {
                                    if (!value || value.trim() === "") {
                                        return "environment name is required.";
                                    }
                                    return null;
                                })
                            },
                        ]
                    }
                ]
            },
            {
                type: 'row',
                classNames: [''],
                children: [
                    {
                        type: 'col',
                        classNames: ['col-auto'],
                        children: [
                            {
                                type: 'submit-button',
                                label: 'Save',
                                dataQa: 'btn-save'
                            }
                        ]
                    },
                    {
                        type: 'col',
                        classNames: ['col-auto'],
                        children: [
                            {
                                type: 'reset-button',
                                label: 'Reset',
                                dataQa: 'btn-reset'
                            }
                        ]
                    }
                ]
            },
        ]
    };
    return (
        <>
            {canManageAppDefaults && <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    App Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage your application settings, including project and environment selection. These settings will determine the context for your feature management and analytics across the platform.
                </Typography>
            </Box>}
            {canManageAppDefaults && <DynamicForm formBuilder={formBuilder} onInit={(service: FormService) => {
                setFormService(service)
            }} onSubmit={async (formData: any) => {
                try {
                    const response = await apiService.put('/user/application-state', formData, {
                        headers: {
                            ProjectKey: formData.selected_project,
                            EnvironmentName: formData.selected_environment,
                        },
                    });
                    const data = response.data?.data;
                    dispatch(updateAppState({
                        project_key: data.selected_project,
                        project_name: data.selected_project_name,
                        environment: data.selected_environment,
                        is_invited: data.is_invited ?? false
                    }));
                    apiService.showSnack('Application settings updated successfully', 'success');
                } catch (err: any) {
                    console.error(err);
                }
            }} />}

            {canManageAppDefaults && (canManageAnonymousConnections || canManageChannelNamespaces) && <Divider sx={{ my: 4 }} />}

            {settingsConfig.filter((config) => config.permission).map((config) => (
                <div key={config.key}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                            {config.title}
                        </Typography>
                    <Box
                        data-qa={config.dataQa}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ pr: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {config.description}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexShrink: 0,
                            }}
                        >
                            <Typography
                                variant="body2"
                                color={organisationSettings[config.key] ? "error.main" : "text.secondary"}
                            >
                                {organisationSettings[config.key] ? config.enabledLabel : config.disabledLabel}
                            </Typography>

                            <Switch
                                checked={organisationSettings[config.key]}
                                onChange={() => handleSettingToggle(config.key, config)}
                                disabled={isSaving}
                                size="small"
                            />
                        </Box>
                    </Box>

                    {config.infoMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="info" variant="outlined">
                                {config.infoMessage}
                            </Alert>
                        </Box>
                    )}

                    <Divider sx={{ my: 4 }} />
                </div>
            ))}
        </>
    )
}, {
    page_title: "General"
})
